import styles from "./MilestoneProgress.module.scss";
import checkIcon from "./icons/check.png";
import checkFreeIcon from "./icons/check-free.png";
import { FC } from "react";
import { countDecimalPlaces, formatNumberWithSpaces } from "../../../../utils/numbers";
import { IChallenge } from "../../../../redux/challenges";
import { omit } from "lodash";
import { Ticket, TicketType } from "../../../../components/TicketsPanel/Ticket";
import { Fragment } from "../../../../components/Fragment/Fragment";
import { CurrencySmallIcon } from "../../../../components/CurrencySmallIcon/CurrencySmallIcon";
import interactiveDrawPrizeIcon from '../../../../icons/prize_gray.jpg';
import automaticDrawPrizeIcon from '../../../../icons/prize_color.jpg';

interface IMilestoneProgressProps extends IChallenge {}

export const MilestoneProgress: FC<IMilestoneProgressProps> = ({ progress, milestones }) => {
    const calculateProgressWidth = (value: number) => {
        if (milestones.length === 0) return 0;
        
        // Если значение меньше или равно первой вехе
        if (value <= milestones[0].threshold) {
            const ratio = value / milestones[0].threshold;
            return ratio * (100 / milestones.length);
        }
        
        // Если значение больше или равно последней вехе
        const lastIndex = milestones.length - 1;
        if (value >= milestones[lastIndex].threshold) {
            return 100;
        }
        
        // Ищем между какими вехами находится значение
        for (let i = 1; i < milestones.length; i++) {
            if (value <= milestones[i].threshold) {
                const prevThreshold = milestones[i - 1].threshold;
                const currentThreshold = milestones[i].threshold;
                
                // Прогресс внутри текущего сегмента (0-1)
                const segmentProgress = (value - prevThreshold) / (currentThreshold - prevThreshold);
                
                // Каждая веха занимает равную часть прогресс-бара
                const segmentWidth = 100 / milestones.length;
                
                // Начало сегмента + прогресс внутри него
                return (i * segmentWidth) + (segmentProgress * segmentWidth);
            }
        }
        
        return 100;
    };

    const progressWidth = calculateProgressWidth(progress);

    const getIcon = (type: string, amount: number) => {
        const precission = countDecimalPlaces(amount);
        const props = { precission, value: amount, type: type as TicketType };
        switch (type) {
            case 'balance':
                return <CurrencySmallIcon value={amount} compact={true} />

            case 'regularTickets':
            case 'rareTickets':
            case 'epicTickets':
            case 'legendaryTickets':
                return <Ticket {...props} />;

            case 'fragments':
            case 'fragmentsDrawPrize':
                return <Fragment amount={amount} />

            case 'interactiveDrawPrize': return (
                <span>
                    <img src={ interactiveDrawPrizeIcon } className={styles.image} alt="Приз интерактивного розыгрыша" />
                    <span className={styles.valueLabel}>
                        { amount }
                    </span>
                </span>
            );
            case 'automaticDrawPrize': return (
                <span>
                    <img src={ automaticDrawPrizeIcon } className={styles.image} alt="Приз автоматического розыгрыша" />
                    <span className={styles.valueLabel}>
                        { amount }
                    </span>
                </span>
            );
        }
    }
    
    return (
        <div className={styles.container}>
            <div className={styles.labels}>
                <div className={styles.description}>
                    <span className={styles.labelText}>Прогресс</span>
                    <span className={styles.labelText}>Награда</span>
                </div>
                
                {milestones.map((milestone, index) => {
                    const rewards = Object.entries(omit(milestone.reward, 'value'));
                    const isCompleted = progress >= milestone.threshold;

                    return (
                        <div className={styles.labelColumn} key={index}>
                            <span className={styles.milestone}>
                                {formatNumberWithSpaces(milestone.threshold)}
                            </span>

                            <img
                                className={styles.checkIcon}
                                src={isCompleted ? checkIcon : checkFreeIcon}
                                alt="✔"
                            />

                            <div className={styles.rewardsContainer}>
                                {rewards.map(([type, amount], rewardIndex) => {
                                    const icon = getIcon(type, amount);
                                    return (
                                        <div key={rewardIndex} className={styles.rewardItem}>
                                            {icon}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className={styles.track}>                
                <div
                    className={styles.progress}
                    style={{ width: `${progressWidth}%` }}
                ></div>
            </div>
        </div>
    );
}